import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FaCertificate, FaRedoAlt, FaSave } from "react-icons/fa";
import BackButton from "../ChildComponent/BackButton";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import MyLoader from "../Spinner/MyLoader";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/certificate.css";

const EMPTY_FORM = {
  certificate_type: "",
  label: "",
  title: "",
  subtitle: "",
  body: "",
  title_font_size: "2.5rem",
  subtitle_font_size: "1.25rem",
  body_font_size: "1.125rem",
  name_font_size: "2.5rem",
  school_name_font_size: "1.75rem",
  icon: "fa-certificate",
  color: "#3b82f6",
  selection_scope: "student",
};

const SIZE_FIELDS = [
  ["title_font_size", "Title size"],
  ["subtitle_font_size", "Subtitle size"],
  ["body_font_size", "Body size"],
  ["name_font_size", "Name size"],
  ["school_name_font_size", "School name size"],
];

//Created By Cursor
const CertificateSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [types, setTypes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [activeType, setActiveType] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all");

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //Created By Cursor
  const fetchTypes = async () => {
    try {
      setLoading(true);
      const schoolCode = SessionManager.getSchoolCode();
      if (!schoolCode) {
        myToast.notify_warning("School code not found");
        return;
      }

      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}getCertificateTypes.php`;
      const response = await axios.get(URL, {
        params: { school_code: schoolCode },
      });

      if (response.data?.api_status === "OK") {
        const rows = response.data.data || [];
        setTypes(rows);
        const next = {};
        rows.forEach((row) => {
          next[row.certificate_type] = { ...EMPTY_FORM, ...row };
        });
        setDrafts(next);
        setActiveType((prev) => {
          if (prev && next[prev]) return prev;
          return rows[0]?.certificate_type || "";
        });
      } else {
        myToast.notify_warning(
          response.data?.api_remark || "Failed to load certificate types"
        );
        setTypes([]);
        setActiveType("");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_error("Failed to load certificate types");
      setTypes([]);
      setActiveType("");
    } finally {
      setLoading(false);
    }
  };

  //Created By Cursor
  const updateDraft = (field, value) => {
    if (!activeType) return;
    setDrafts((prev) => ({
      ...prev,
      [activeType]: {
        ...(prev[activeType] || EMPTY_FORM),
        [field]: value,
      },
    }));
  };

  //Created By Cursor
  const saveType = async () => {
    const draft = drafts[activeType];
    if (!draft) return;

    if (!draft.title || !draft.body) {
      myToast.notify_warning("Title and body are required");
      return;
    }

    try {
      setSaving(true);
      const schoolCode = SessionManager.getSchoolCode();
      const staff = SessionManager.getStaffDetails() || {};
      const URL = `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}updateCertificateType.php`;

      const response = await axios.get(URL, {
        params: {
          school_code: schoolCode,
          certificate_type: draft.certificate_type,
          label: draft.label || "",
          title: draft.title || "",
          subtitle: draft.subtitle || "",
          body: draft.body || "",
          title_font_size: draft.title_font_size || "2.5rem",
          subtitle_font_size: draft.subtitle_font_size || "1.25rem",
          body_font_size: draft.body_font_size || "1.125rem",
          name_font_size: draft.name_font_size || "2.5rem",
          school_name_font_size: draft.school_name_font_size || "1.75rem",
          staff_id: staff.staff_id || "",
          staff_name: staff.staff_name || "",
        },
      });

      if (response.data?.api_status === "OK") {
        myToast.notify_success("Saved successfully");
        fetchTypes();
      } else {
        myToast.notify_warning(response.data?.api_remark || "Failed to save");
      }
    } catch (error) {
      console.error(error);
      myToast.notify_error("Failed to save certificate type");
    } finally {
      setSaving(false);
    }
  };

  const filteredTypes = useMemo(() => {
    if (scopeFilter === "all") return types;
    return types.filter((t) => t.selection_scope === scopeFilter);
  }, [types, scopeFilter]);

  const draft = drafts[activeType] || EMPTY_FORM;
  const activeMeta = types.find((t) => t.certificate_type === activeType);

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div className="content event-certificate cs-page">
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <a href="/AdminMain/Settings" style={{ color: COLORS.white }}>
                Settings
              </a>
            </li>
            <li>
              <span style={{ color: COLORS.white }}>Certificate Settings</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="page-card">
        <div className="header-section">
          <div className="header-content">
            <h5>Certificate Settings</h5>
            <small>
              Manage certificate text and font sizes for each certificate type
            </small>
          </div>
          <div className="cs-header-actions">
            <button
              type="button"
              className="selection-button"
              onClick={fetchTypes}
              style={{
                background: "rgba(255,255,255,0.18)",
                boxShadow: "none",
              }}
            >
              <FaRedoAlt />
              Refresh
            </button>
            <div className="header-icon">
              <FaCertificate />
            </div>
          </div>
        </div>

        <div className="cs-body">
          {types.length === 0 ? (
            <div className="cs-empty">
              <i className="fa fa-database" />
              <h6>No certificate types found</h6>
              <p>
                Run <code>tm_certificate_type.sql</code> on the database, then
                refresh.
              </p>
            </div>
          ) : (
            <>
              <div className="selection-section cs-filter-section">
                <h5 className="section-title">
                  <i className="fa fa-filter" style={{ marginRight: 6 }} />
                  Scope
                </h5>
                <div className="cs-scope-chips">
                  {[
                    ["all", "All"],
                    ["student", "Student"],
                    ["staff", "Staff"],
                  ].map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      className={`cs-chip${scopeFilter === value ? " active" : ""}`}
                      onClick={() => {
                        setScopeFilter(value);
                        const nextList =
                          value === "all"
                            ? types
                            : types.filter((t) => t.selection_scope === value);
                        if (
                          nextList.length &&
                          !nextList.some((t) => t.certificate_type === activeType)
                        ) {
                          setActiveType(nextList[0].certificate_type);
                        }
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="selection-section cs-type-list">
                <h5 className="section-title">
                  <i className="fa fa-award" style={{ marginRight: 6 }} />
                  Certificate Types
                </h5>
                <div className="cs-type-row">
                  {filteredTypes.map((type) => {
                    const item = drafts[type.certificate_type] || type;
                    const isActive = activeType === type.certificate_type;
                    return (
                      <button
                        key={type.certificate_type}
                        type="button"
                        className={`cs-type-item${isActive ? " active" : ""}`}
                        style={{
                          borderColor: isActive
                            ? item.color || "#3b82f6"
                            : undefined,
                          background: isActive
                            ? `${item.color || "#3b82f6"}12`
                            : undefined,
                        }}
                        onClick={() => setActiveType(type.certificate_type)}
                      >
                        <span
                          className="cs-type-icon"
                          style={{ background: item.color || "#3b82f6" }}
                        >
                          <i className={`fa ${item.icon || "fa-certificate"}`} />
                        </span>
                        <span className="cs-type-meta">
                          <strong>{item.label || type.certificate_type}</strong>
                          <small>
                            {type.selection_scope}
                            {type.has_school_override === "1"
                              ? " · override"
                              : " · default"}
                          </small>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <section className="selection-section cs-editor">
                  <div className="cs-editor-head">
                    <div>
                      <h5 className="section-title" style={{ marginBottom: 4 }}>
                        <i
                          className={`fa ${draft.icon || "fa-certificate"}`}
                          style={{
                            marginRight: 6,
                            color: draft.color || COLORS.primary_color,
                          }}
                        />
                        {draft.label || "Certificate"}
                      </h5>
                      <small className="cs-editor-sub">
                        {draft.certificate_type}
                        {activeMeta?.has_school_override === "1"
                          ? " · school override active"
                          : " · using master default until saved"}
                        {activeMeta?.updated_by_name
                          ? ` · updated by ${activeMeta.updated_by_name}`
                          : activeMeta?.added_by_name
                          ? ` · added by ${activeMeta.added_by_name}`
                          : ""}
                      </small>
                    </div>
                    <button
                      type="button"
                      className="selection-button"
                      disabled={saving || !activeType}
                      onClick={saveType}
                      style={{
                        background: `linear-gradient(135deg, ${
                          draft.color || "#3b82f6"
                        } 0%, #1d4ed8 100%)`,
                      }}
                    >
                      <FaSave />
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </div>

                  <div className="cs-form-grid">
                    <label className="cs-field">
                      <span>Label</span>
                      <input
                        value={draft.label || ""}
                        onChange={(e) => updateDraft("label", e.target.value)}
                      />
                    </label>
                    <label className="cs-field">
                      <span>Title</span>
                      <input
                        value={draft.title || ""}
                        onChange={(e) => updateDraft("title", e.target.value)}
                      />
                    </label>
                    <label className="cs-field cs-field-full">
                      <span>Subtitle</span>
                      <input
                        value={draft.subtitle || ""}
                        onChange={(e) =>
                          updateDraft("subtitle", e.target.value)
                        }
                      />
                    </label>
                    <label className="cs-field cs-field-full">
                      <span>Body text</span>
                      <textarea
                        rows={4}
                        value={draft.body || ""}
                        onChange={(e) => updateDraft("body", e.target.value)}
                      />
                    </label>
                  </div>

                  <div className="cs-size-block">
                    <h6>Font sizes</h6>
                    <div className="cs-size-grid">
                      {SIZE_FIELDS.map(([field, caption]) => (
                        <label key={field} className="cs-field">
                          <span>{caption}</span>
                          <input
                            placeholder="e.g. 2.5rem"
                            value={draft[field] || ""}
                            onChange={(e) =>
                              updateDraft(field, e.target.value)
                            }
                          />
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="cs-preview">
                    <div className="cs-preview-label">Live preview</div>
                    <div
                      className="cs-preview-title"
                      style={{
                        fontSize: draft.title_font_size || "1.5rem",
                        color: draft.color || "#1e3a8a",
                      }}
                    >
                      {draft.title || "Title preview"}
                    </div>
                    <div
                      className="cs-preview-subtitle"
                      style={{
                        fontSize: draft.subtitle_font_size || "1rem",
                      }}
                    >
                      {draft.subtitle || "Subtitle preview"}
                    </div>
                    <div
                      className="cs-preview-name"
                      style={{
                        fontSize: draft.name_font_size || "1.5rem",
                        color: draft.color || "#1e3a8a",
                      }}
                    >
                      RECIPIENT NAME
                    </div>
                    <div
                      className="cs-preview-body"
                      style={{
                        fontSize: draft.body_font_size || "1rem",
                      }}
                    >
                      {draft.body || "Body preview"}
                    </div>
                    <div
                      className="cs-preview-school"
                      style={{
                        fontSize: draft.school_name_font_size || "1.25rem",
                      }}
                    >
                      SCHOOL NAME
                    </div>
                  </div>
                </section>
            </>
          )}
        </div>
      </div>

      <style>{`
        .cs-page .page-card { margin: 0 0 24px; }
        .cs-header-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .cs-body {
          padding: 16px 20px 24px;
        }
        .cs-filter-section {
          margin-bottom: 16px;
        }
        .cs-scope-chips {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .cs-chip {
          border: 1px solid #cbd5e1;
          background: #fff;
          color: #475569;
          border-radius: 999px;
          padding: 7px 14px;
          font-size: 13px;
          font-weight: 700;
          cursor: pointer;
        }
        .cs-chip.active {
          background: linear-gradient(135deg, #3b82f6 0%, #6366f1 100%);
          border-color: transparent;
          color: #fff;
        }
        .cs-type-list {
          margin-bottom: 16px;
        }
        .cs-type-row {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .cs-type-item {
          display: flex;
          align-items: center;
          gap: 10px;
          flex: 1 1 180px;
          max-width: 240px;
          text-align: left;
          border: 1px solid #e2e8f0;
          background: #fff;
          border-radius: 10px;
          padding: 10px 12px;
          cursor: pointer;
          transition: all 0.15s ease;
        }
        .cs-type-item:hover {
          border-color: #93c5fd;
          transform: translateY(-1px);
        }
        .cs-type-item.active {
          box-shadow: 0 6px 16px rgba(59,130,246,0.12);
        }
        .cs-editor {
          margin-top: 0;
        }
        .cs-type-icon {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .cs-type-meta {
          display: flex;
          flex-direction: column;
          min-width: 0;
        }
        .cs-type-meta strong {
          font-size: 13px;
          color: #0f172a;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        .cs-type-meta small {
          font-size: 11px;
          color: #64748b;
          text-transform: capitalize;
        }
        .cs-editor-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 14px;
        }
        .cs-editor-sub {
          color: #64748b;
          font-size: 12px;
        }
        .cs-form-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 12px;
        }
        .cs-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin: 0;
        }
        .cs-field-full {
          grid-column: 1 / -1;
        }
        .cs-field span {
          font-size: 12px;
          font-weight: 700;
          color: #475569;
        }
        .cs-field input,
        .cs-field textarea {
          width: 100%;
          border: 1px solid #dbe3ee;
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 14px;
          color: #0f172a;
          background: #fff;
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .cs-field input:focus,
        .cs-field textarea:focus {
          border-color: #3b82f6;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15);
        }
        .cs-size-block {
          margin-top: 16px;
          padding-top: 14px;
          border-top: 1px dashed #e2e8f0;
        }
        .cs-size-block h6 {
          margin: 0 0 10px;
          font-size: 13px;
          font-weight: 800;
          color: #334155;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .cs-size-grid {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 10px;
        }
        .cs-preview {
          margin-top: 16px;
          padding: 18px;
          border-radius: 12px;
          border: 1px solid #dbeafe;
          background:
            linear-gradient(180deg, rgba(59,130,246,0.06), rgba(255,255,255,0.9)),
            #fff;
          text-align: center;
        }
        .cs-preview-label {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
          margin-bottom: 10px;
        }
        .cs-preview-title {
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .cs-preview-subtitle {
          margin-top: 6px;
          color: #b91c1c;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .cs-preview-name {
          margin-top: 14px;
          font-weight: 800;
          letter-spacing: 0.08em;
        }
        .cs-preview-body {
          margin-top: 10px;
          color: #334155;
          line-height: 1.55;
        }
        .cs-preview-school {
          margin-top: 14px;
          font-weight: 700;
          color: #1e3a8a;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }
        .cs-empty {
          text-align: center;
          padding: 48px 20px;
          border: 1px dashed #cbd5e1;
          border-radius: 12px;
          color: #64748b;
        }
        .cs-empty i {
          font-size: 28px;
          margin-bottom: 10px;
          color: #94a3b8;
        }
        .cs-empty h6 {
          margin: 0 0 6px;
          font-weight: 800;
          color: #334155;
        }
        .cs-empty p {
          margin: 0;
        }
        @media (max-width: 992px) {
          .cs-size-grid {
            grid-template-columns: repeat(2, minmax(0, 1fr));
          }
          .cs-type-item {
            max-width: none;
          }
        }
        @media (max-width: 576px) {
          .cs-form-grid,
          .cs-size-grid {
            grid-template-columns: 1fr;
          }
          .cs-editor-head {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default CertificateSettings;
